/**
 * View Step Renderer - Shows a page within the guided flow
 *
 * Renders the actual page component full-width. The ChatPanel is managed
 * by GuidedFlow (outside the animation layer) so it doesn't flash on transitions.
 */

import React, { useEffect, useState } from 'react';
import { FlowViewConfig, FlowContext } from '../core/types';
import { GuidedFlowController } from '../core/GuidedFlowController';
import { AnimatePresence, motion } from 'motion/react';
import { getViewComponent } from './ViewRegistry';

export interface ViewStepRendererProps {
  config: FlowViewConfig;
  context: FlowContext;
  controller: GuidedFlowController;
  onNext: () => void;
  autoAdvance?: boolean;
  autoAdvanceDelay?: number;
}

export const ViewStepRenderer: React.FC<ViewStepRendererProps> = ({
  config,
  context: _context,
  controller,
  onNext,
  autoAdvance,
  autoAdvanceDelay,
}) => {
  const [_isLoaded, setIsLoaded] = useState(false);
  const providers = controller.getProviders();

  const ViewComponent = config.component ? getViewComponent(config.component) : null;

  // Navigate + apply annotations
  useEffect(() => {
    const setupView = async () => {
      if (config.component) {
        await providers.navigation.navigateToView(config.component, config.props);
      }

      if (config.annotations) {
        config.annotations.forEach((annotation) => {
          let highlightType: 'spotlight' | 'outline' | 'pulse' | undefined;
          if (annotation.type === 'spotlight' || annotation.type === 'pulse') {
            highlightType = annotation.type;
          } else if (annotation.type === 'highlight') {
            highlightType = 'outline';
          } else {
            highlightType = 'spotlight';
          }
          providers.ui.highlightElement(annotation.target, {
            type: highlightType,
            color: annotation.style?.color,
          });
        });
      }

      setIsLoaded(true);
    };

    setupView().catch(console.error);

    return () => {
      if (config.annotations) {
        config.annotations.forEach((annotation) => {
          providers.ui.removeHighlight(annotation.id);
        });
      }
    };
  }, [config, providers]);

  // Auto-advance after a delay (e.g., for the OAuth step)
  useEffect(() => {
    if (!autoAdvance || !autoAdvanceDelay) return;
    const timer = setTimeout(onNext, autoAdvanceDelay);
    return () => clearTimeout(timer);
  }, [autoAdvance, autoAdvanceDelay, onNext]);

  // Don't show the floating chat for chat-interface views (they ARE the chat)
  const isChatView = config.component === 'chat-interface';

  // For chat-interface views, inject onScriptComplete so advance actions work
  const viewProps = isChatView
    ? { ...(config.props || {}), onScriptComplete: onNext }
    : (config.props || {});

  return (
    <div className="h-full relative">
      {ViewComponent && <ViewComponent {...viewProps} />}

      {/* Interaction hints overlay */}
      {config.hints && config.hints.length > 0 && (
        <div className="absolute inset-0 pointer-events-none z-40">
          <AnimatePresence>
            {config.hints.map((hint) => (
              <motion.div
                key={hint.id}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.3, delay: 0.5 }}
                className="absolute pointer-events-auto"
              >
                {hint.animated && (
                  <motion.div
                    animate={{ y: [0, -10, 0] }}
                    transition={{ repeat: Infinity, duration: 1.5 }}
                    className="bg-primary text-white px-4 py-2 rounded-lg shadow-lg"
                  >
                    {hint.action}
                  </motion.div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};
